// ReviewAndCompare — iter180l
// Side-by-side P&L statement viewer. Renders TWO outlet blocks (from
// /api/admin/pnl/period/* + /api/admin/pnl/compare) using the SAME
// PnLStatementBody renderer that mirrors MTD by Outlet section ordering.
//
// PRINCIPLE: this component issues read requests; it never computes.
// All numbers — including ratios and aggregates — come from the backend.

import { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Loader2, ArrowLeftRight, Calendar, Layers, GitCompareArrows, FileSpreadsheet, FileText } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import PnLStatementBody from './PnLStatementBody';
import { exportStatementXlsx, exportStatementPdf } from './pnlExporters';
import {
  MONTH_NAMES, fyChoices, fyLabel, fyStartYearFor, fyQuarterLabel, fyYtdLabel,
} from './fiscalYear';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/** Build a clean export file name like
 *  "PnL_WoodlandEatery_May2026_vs_Jun2026.pdf". */
function buildExportFileName(result, ext) {
  const outletName = (result?.period_a?.block?.outlet_name || 'Outlet').replace(/[^\w]+/g, '');
  const tag = (block, spec) => {
    if (spec.kind === 'month') return `${['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][spec.month-1]}${spec.year}`;
    if (spec.kind === 'quarter') return `FY${spec.fy_start_year}Q${spec.quarter}`;
    if (spec.kind === 'ytd') return `YTD-FY${spec.fy_start_year}-${spec.through_month}`;
    if (spec.kind === 'budget_mtd') return `Budget${['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][spec.month-1]}${spec.year}`;
    if (spec.kind === 'budget_ytd') return `BudgetYTD-FY${spec.fy_start_year}`;
    return 'Period';
  };
  const a = tag(result.period_a.block, result.period_a.spec);
  const b = tag(result.period_b.block, result.period_b.spec);
  return `PnL_${outletName}_${a}_vs_${b}.${ext}`;
}

const MODES = [
  { value: 'mvm',     label: 'Month vs Month',                    icon: ArrowLeftRight },
  { value: 'avb',     label: 'Actual vs Budget (single month)',   icon: GitCompareArrows },
  { value: 'cm_pm',   label: 'Current Month vs Previous Month',   icon: ArrowLeftRight },
  { value: 'cm_lyly', label: 'Current Month vs Same Month Last Year', icon: ArrowLeftRight },
  { value: 'qvq',     label: 'Quarter vs Quarter',                icon: Layers },
  { value: 'ytd_avb', label: 'YTD Actual vs YTD Budget',          icon: GitCompareArrows },
  { value: 'ytd_pyytd', label: 'YTD vs Previous Year YTD',        icon: ArrowLeftRight },
];

export default function ReviewAndCompare() {
  const { getHeaders } = useAuth();
  const headers = useMemo(() => getHeaders(), [getHeaders]);
  const now = new Date();
  const curY = now.getFullYear();
  const curM = now.getMonth() + 1;
  const currentFy = fyStartYearFor(curY, curM);

  const [outlets, setOutlets] = useState([]);
  const [outletId, setOutletId] = useState('');
  const [mode, setMode] = useState('mvm');

  // Month-vs-Month selectors
  const [aYear, setAYear]   = useState(curY);
  const [aMonth, setAMonth] = useState(curM === 1 ? 12 : curM - 1);
  const [bYear, setBYear]   = useState(curY);
  const [bMonth, setBMonth] = useState(curM);

  // Quarter-vs-Quarter
  const [aFy, setAFy] = useState(currentFy);
  const [aQ, setAQ]   = useState(1);
  const [bFy, setBFy] = useState(currentFy);
  const [bQ, setBQ]   = useState(2);

  // YTD picker
  const [ytdFy, setYtdFy]                 = useState(currentFy);
  const [ytdThroughMonth, setYtdThroughMonth] = useState(curM);

  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  // Load outlets on mount
  useEffect(() => {
    axios.get(`${API}/admin/outlets`, { headers })
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : [];
        setOutlets(list);
        if (list.length && !outletId) setOutletId(list[0].id);
      })
      .catch(() => toast.error('Failed to load outlets'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Build spec from mode + selectors ───────────────────────────────
  const buildSpec = useCallback(() => {
    if (!outletId) return null;
    const id = outletId;
    switch (mode) {
      case 'mvm':
        return {
          period_a: { kind: 'month', source: 'auto', outlet_id: id, year: aYear, month: aMonth },
          period_b: { kind: 'month', source: 'auto', outlet_id: id, year: bYear, month: bMonth },
        };
      case 'avb':
        return {
          period_a: { kind: 'budget_mtd', outlet_id: id, year: bYear, month: bMonth },
          period_b: { kind: 'month', source: 'auto', outlet_id: id, year: bYear, month: bMonth },
        };
      case 'cm_pm': {
        const prevM = curM === 1 ? 12 : curM - 1;
        const prevY = curM === 1 ? curY - 1 : curY;
        return {
          period_a: { kind: 'month', source: 'auto', outlet_id: id, year: prevY, month: prevM },
          period_b: { kind: 'month', source: 'auto', outlet_id: id, year: curY, month: curM },
        };
      }
      case 'cm_lyly':
        return {
          period_a: { kind: 'month', source: 'auto', outlet_id: id, year: curY - 1, month: curM },
          period_b: { kind: 'month', source: 'auto', outlet_id: id, year: curY,     month: curM },
        };
      case 'qvq':
        return {
          period_a: { kind: 'quarter', source: 'auto', outlet_id: id, fy_start_year: aFy, quarter: aQ },
          period_b: { kind: 'quarter', source: 'auto', outlet_id: id, fy_start_year: bFy, quarter: bQ },
        };
      case 'ytd_avb':
        return {
          period_a: { kind: 'budget_ytd', outlet_id: id, fy_start_year: ytdFy, through_month: ytdThroughMonth },
          period_b: { kind: 'ytd', source: 'auto', outlet_id: id, fy_start_year: ytdFy, through_month: ytdThroughMonth },
        };
      case 'ytd_pyytd':
        return {
          period_a: { kind: 'ytd', source: 'auto', outlet_id: id, fy_start_year: ytdFy - 1, through_month: ytdThroughMonth },
          period_b: { kind: 'ytd', source: 'auto', outlet_id: id, fy_start_year: ytdFy,     through_month: ytdThroughMonth },
        };
      default:
        return null;
    }
  }, [mode, outletId, aYear, aMonth, bYear, bMonth, aFy, aQ, bFy, bQ, ytdFy, ytdThroughMonth, curY, curM]);

  const runCompare = useCallback(async () => {
    const spec = buildSpec();
    if (!spec) { toast.error('Pick an outlet first'); return; }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/admin/pnl/compare`, spec, { headers });
      setResult(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Comparison failed');
      setResult(null);
    } finally { setLoading(false); }
  }, [buildSpec, headers]);

  // Auto-run when key inputs change
  useEffect(() => { if (outletId) runCompare(); /* eslint-disable-next-line */ }, [mode, outletId]);

  // ── Labels for the column headers ──────────────────────────────────
  const colLabels = useMemo(() => {
    if (!result) return { a: 'A', b: 'B' };
    const specA = result.period_a.spec; const specB = result.period_b.spec;
    const labelOf = (spec, block) => {
      const src = block?._review_source || 'live';
      const srcBadge = src === 'finalized' ? ' · Finalized'
                    : src === 'budget'     ? ''
                    : src === 'aggregate'  ? ' · Aggregated'
                    : ' · Live';
      if (spec.kind === 'month') return `${MONTH_NAMES[spec.month - 1]} ${spec.year}${srcBadge}`;
      if (spec.kind === 'quarter') return fyQuarterLabel(spec.fy_start_year, spec.quarter) + srcBadge;
      if (spec.kind === 'ytd') return fyYtdLabel(spec.fy_start_year, spec.through_month) + srcBadge;
      if (spec.kind === 'budget_mtd') return `Budget · ${MONTH_NAMES[spec.month - 1]} ${spec.year}`;
      if (spec.kind === 'budget_ytd') return `Budget · YTD FY ${fyLabel(spec.fy_start_year)} (Apr→${MONTH_NAMES[spec.through_month - 1]})`;
      return 'Period';
    };
    return {
      a: labelOf(specA, result.period_a.block),
      b: labelOf(specB, result.period_b.block),
    };
  }, [result]);

  const outletOptions = outlets.map((o) => ({ value: o.id, label: o.name || o.outlet_name }));
  const fyOptions = fyChoices(curY, curM);

  return (
    <div className="space-y-4" data-testid="review-compare-root">
      {/* Picker */}
      <Card className="border-[#E2E8DF]">
        <CardHeader className="py-2.5 px-4 border-b border-[#E2E8DF] bg-[#F9F9F8]">
          <CardTitle className="text-sm font-semibold text-[color:var(--color-text-primary)] flex items-center gap-2">
            <GitCompareArrows className="w-4 h-4 text-[color:var(--color-primary)]" /> Review &amp; Compare
            <span className="ml-2 text-[10.5px] text-[#9CA3A0] font-normal">
              Same MTD statement · two periods side by side · no recomputation
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 py-3 space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            {/* Mode */}
            <div className="space-y-1">
              <label className="text-[10.5px] uppercase tracking-wider text-[#9CA3A0]">Mode</label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger className="h-9 w-[260px] text-[12.5px]" data-testid="rc-mode-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MODES.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Outlet */}
            <div className="space-y-1">
              <label className="text-[10.5px] uppercase tracking-wider text-[#9CA3A0]">Outlet</label>
              <Select value={outletId} onValueChange={setOutletId}>
                <SelectTrigger className="h-9 w-[220px] text-[12.5px]" data-testid="rc-outlet-select">
                  <SelectValue placeholder="Choose…" />
                </SelectTrigger>
                <SelectContent>
                  {outletOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Per-mode selectors */}
            {(mode === 'mvm' || mode === 'avb') && (
              <>
                {mode === 'mvm' && (
                  <div className="flex items-end gap-2" data-testid="rc-mvm-a">
                    <div className="space-y-1">
                      <label className="text-[10.5px] uppercase tracking-wider text-[#9CA3A0]">A — Month</label>
                      <Select value={String(aMonth)} onValueChange={(v) => setAMonth(parseInt(v))}>
                        <SelectTrigger className="h-9 w-[100px] text-[12.5px]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {MONTH_NAMES.map((n, i) => <SelectItem key={i} value={String(i + 1)}>{n}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10.5px] uppercase tracking-wider text-[#9CA3A0]">Year</label>
                      <Select value={String(aYear)} onValueChange={(v) => setAYear(parseInt(v))}>
                        <SelectTrigger className="h-9 w-[88px] text-[12.5px]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {[curY - 2, curY - 1, curY, curY + 1].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
                <div className="flex items-end gap-2" data-testid="rc-mvm-b">
                  <div className="space-y-1">
                    <label className="text-[10.5px] uppercase tracking-wider text-[#9CA3A0]">{mode === 'mvm' ? 'B — Month' : 'Period — Month'}</label>
                    <Select value={String(bMonth)} onValueChange={(v) => setBMonth(parseInt(v))}>
                      <SelectTrigger className="h-9 w-[100px] text-[12.5px]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {MONTH_NAMES.map((n, i) => <SelectItem key={i} value={String(i + 1)}>{n}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10.5px] uppercase tracking-wider text-[#9CA3A0]">Year</label>
                    <Select value={String(bYear)} onValueChange={(v) => setBYear(parseInt(v))}>
                      <SelectTrigger className="h-9 w-[88px] text-[12.5px]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {[curY - 2, curY - 1, curY, curY + 1].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </>
            )}

            {mode === 'qvq' && (
              <>
                <div className="flex items-end gap-2" data-testid="rc-qvq-a">
                  <div className="space-y-1">
                    <label className="text-[10.5px] uppercase tracking-wider text-[#9CA3A0]">A — FY</label>
                    <Select value={String(aFy)} onValueChange={(v) => setAFy(parseInt(v))}>
                      <SelectTrigger className="h-9 w-[110px] text-[12.5px]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {fyOptions.map((y) => <SelectItem key={y} value={String(y)}>FY {fyLabel(y)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10.5px] uppercase tracking-wider text-[#9CA3A0]">Q</label>
                    <Select value={String(aQ)} onValueChange={(v) => setAQ(parseInt(v))}>
                      <SelectTrigger className="h-9 w-[80px] text-[12.5px]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {[1, 2, 3, 4].map((q) => <SelectItem key={q} value={String(q)}>Q{q}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex items-end gap-2" data-testid="rc-qvq-b">
                  <div className="space-y-1">
                    <label className="text-[10.5px] uppercase tracking-wider text-[#9CA3A0]">B — FY</label>
                    <Select value={String(bFy)} onValueChange={(v) => setBFy(parseInt(v))}>
                      <SelectTrigger className="h-9 w-[110px] text-[12.5px]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {fyOptions.map((y) => <SelectItem key={y} value={String(y)}>FY {fyLabel(y)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10.5px] uppercase tracking-wider text-[#9CA3A0]">Q</label>
                    <Select value={String(bQ)} onValueChange={(v) => setBQ(parseInt(v))}>
                      <SelectTrigger className="h-9 w-[80px] text-[12.5px]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {[1, 2, 3, 4].map((q) => <SelectItem key={q} value={String(q)}>Q{q}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </>
            )}

            {(mode === 'ytd_avb' || mode === 'ytd_pyytd') && (
              <div className="flex items-end gap-2" data-testid="rc-ytd">
                <div className="space-y-1">
                  <label className="text-[10.5px] uppercase tracking-wider text-[#9CA3A0]">FY</label>
                  <Select value={String(ytdFy)} onValueChange={(v) => setYtdFy(parseInt(v))}>
                    <SelectTrigger className="h-9 w-[120px] text-[12.5px]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {fyOptions.map((y) => <SelectItem key={y} value={String(y)}>FY {fyLabel(y)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <label className="text-[10.5px] uppercase tracking-wider text-[#9CA3A0]">Through</label>
                  <Select value={String(ytdThroughMonth)} onValueChange={(v) => setYtdThroughMonth(parseInt(v))}>
                    <SelectTrigger className="h-9 w-[100px] text-[12.5px]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3].map((m) => (
                        <SelectItem key={m} value={String(m)}>{MONTH_NAMES[m - 1]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            <Button onClick={runCompare} disabled={loading || !outletId}
                    className="bg-[color:var(--color-primary)] hover:bg-[#0B2A22] text-white h-9 text-[12.5px]"
                    data-testid="rc-run-btn">
              {loading ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Loading…</>
                       : <><Calendar className="w-3.5 h-3.5 mr-1.5" /> Compare</>}
            </Button>

            {result && (
              <div className="flex items-end gap-1.5" data-testid="rc-export-btns">
                <Button
                  variant="outline" size="sm"
                  onClick={() => exportStatementXlsx({
                    fileName: buildExportFileName(result, 'xlsx'),
                    title: 'Review & Compare — P&L Statement',
                    subtitle: `${colLabels.a}   vs   ${colLabels.b}`,
                    columns: [
                      { label: colLabels.a, block: result.period_a.block },
                      { label: colLabels.b, block: result.period_b.block },
                    ],
                    showDeltaColumn: true,
                    deltaIndex: { aIdx: 0, bIdx: 1 },
                  })}
                  className="h-9 text-[12px] border-[#E2E8DF] text-[color:var(--color-primary)]"
                  data-testid="rc-export-xlsx">
                  <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" /> Excel
                </Button>
                <Button
                  variant="outline" size="sm"
                  onClick={() => exportStatementPdf({
                    fileName: buildExportFileName(result, 'pdf'),
                    title: 'Review & Compare — P&L Statement',
                    subtitle: `${colLabels.a}   vs   ${colLabels.b}`,
                    columns: [
                      { label: colLabels.a, block: result.period_a.block },
                      { label: colLabels.b, block: result.period_b.block },
                    ],
                    showDeltaColumn: true,
                    deltaIndex: { aIdx: 0, bIdx: 1 },
                  })}
                  className="h-9 text-[12px] border-[#E2E8DF] text-[color:var(--color-primary)]"
                  data-testid="rc-export-pdf">
                  <FileText className="w-3.5 h-3.5 mr-1.5" /> PDF
                </Button>
              </div>
            )}
          </div>

          {/* Architectural rule reminder banner */}
          <div className="text-[10.5px] text-[color:var(--color-text-muted)] bg-[#F4F7F4] border border-[#E2E8DF] rounded px-3 py-1.5 italic">
            Values displayed here come straight from the MTD engine or a frozen finalized snapshot.
            Aggregates (Quarter / YTD) sum existing fields. No alternate P&amp;L calculations are performed.
          </div>
        </CardContent>
      </Card>

      {/* Statement */}
      {result ? (
        <PnLStatementBody
          blockA={result.period_a.block}
          blockB={result.period_b.block}
          delta={result.delta}
          labelA={colLabels.a}
          labelB={colLabels.b}
        />
      ) : (
        <div className="text-[12.5px] text-[#9CA3A0] italic px-3 py-6 text-center bg-white border border-dashed border-[#E2E8DF] rounded-lg">
          Pick a mode and outlet, then click <b>Compare</b>.
        </div>
      )}
    </div>
  );
}
